import * as THREE from 'three';

export type RangerPupId =
  | 'arlo'
  | 'pasha'
  | 'liam'
  | 'milo'
  | 'zara'
  | 'tuk'
  | 'mara'
  | 'rowan'
  | 'juniper'
  | 'ember';

export type CharacterMotion = 'idle' | 'walk' | 'run' | 'sneak';
export type CharacterGesture = 'none' | 'wave' | 'point' | 'cheer' | 'listen';
export type CharacterExpression = 'neutral' | 'happy' | 'curious' | 'concerned' | 'determined';

export type RangerPupProfile = {
  id: RangerPupId;
  displayName: string;
  role: string;
  fur: THREE.ColorRepresentation;
  muzzle: THREE.ColorRepresentation;
  innerEar: THREE.ColorRepresentation;
  vest: THREE.ColorRepresentation;
  scarf: THREE.ColorRepresentation;
  trim: THREE.ColorRepresentation;
  eye: THREE.ColorRepresentation;
  scale: number;
  earTilt: number;
  tailCurl: number;
  accessory: 'map' | 'goggles' | 'satchel' | 'radio' | 'canteen' | 'flower' | 'camera';
};

export type RangerPupOptions = {
  castShadow?: boolean;
  scale?: number;
  initialExpression?: CharacterExpression;
};

export type CharacterAnimationInput = {
  motion?: CharacterMotion;
  gesture?: CharacterGesture;
  expression?: CharacterExpression;
  speed?: number;
  talk?: number;
  blink?: boolean;
  lookTarget?: THREE.Vector3 | null;
};

export type CharacterDiagnostics = {
  meshes: number;
  geometries: number;
  materials: number;
  triangles: number;
  bones: number;
};

export type RangerPupRig = {
  hips: THREE.Group;
  chest: THREE.Group;
  neck: THREE.Group;
  head: THREE.Group;
  muzzle: THREE.Group;
  jaw: THREE.Group;
  leftEar: THREE.Group;
  rightEar: THREE.Group;
  leftEye: THREE.Group;
  rightEye: THREE.Group;
  leftShoulder: THREE.Group;
  rightShoulder: THREE.Group;
  leftElbow: THREE.Group;
  rightElbow: THREE.Group;
  leftWrist: THREE.Group;
  rightWrist: THREE.Group;
  leftHip: THREE.Group;
  rightHip: THREE.Group;
  leftKnee: THREE.Group;
  rightKnee: THREE.Group;
  leftAnkle: THREE.Group;
  rightAnkle: THREE.Group;
  tail: THREE.Group;
  backpack: THREE.Group;
  propSocket: THREE.Group;
};

const PROFILES: Record<RangerPupId, RangerPupProfile> = {
  arlo: {
    id: 'arlo', displayName: 'Arlo', role: 'Trail captain', fur: '#737986', muzzle: '#d7d8d7', innerEar: '#b98891',
    vest: '#4b6681', scarf: '#3d88c7', trim: '#edf1e6', eye: '#567c8e', scale: 1, earTilt: 0.05, tailCurl: 0.2, accessory: 'satchel',
  },
  pasha: {
    id: 'pasha', displayName: 'Pasha', role: 'Tinkerer', fur: '#666875', muzzle: '#d8d0ca', innerEar: '#b88791',
    vest: '#6d4d9e', scarf: '#42c5af', trim: '#f4d56b', eye: '#70d3cf', scale: 0.94, earTilt: 0.14, tailCurl: 0.34, accessory: 'goggles',
  },
  liam: {
    id: 'liam', displayName: 'Liam', role: 'Wildlife scout', fur: '#d8b584', muzzle: '#f5e2c6', innerEar: '#d79283',
    vest: '#315f91', scarf: '#e85f45', trim: '#f3eee0', eye: '#426c42', scale: 0.9, earTilt: -0.03, tailCurl: 0.12, accessory: 'satchel',
  },
  milo: {
    id: 'milo', displayName: 'Milo', role: 'Creek navigator', fur: '#b7784e', muzzle: '#efd0a6', innerEar: '#c98278',
    vest: '#397f73', scarf: '#e9ad3e', trim: '#f4edda', eye: '#3d6257', scale: 0.93, earTilt: 0.08, tailCurl: 0.2, accessory: 'map',
  },
  zara: {
    id: 'zara', displayName: 'Zara', role: 'Sky lookout', fur: '#d3c3ad', muzzle: '#f3e6d4', innerEar: '#ca8f98',
    vest: '#8a508e', scarf: '#47aaad', trim: '#f2dc76', eye: '#4e817d', scale: 0.92, earTilt: 0.16, tailCurl: 0.32, accessory: 'goggles',
  },
  tuk: {
    id: 'tuk', displayName: 'Tuk', role: 'Track finder', fur: '#4c4e56', muzzle: '#aaa9ab', innerEar: '#9b6d78',
    vest: '#bd6139', scarf: '#e5bf4f', trim: '#e9e4d6', eye: '#c99142', scale: 0.89, earTilt: -0.05, tailCurl: 0.15, accessory: 'camera',
  },
  mara: {
    id: 'mara', displayName: 'Mara', role: 'Station chief', fur: '#8c5841', muzzle: '#dfb991', innerEar: '#b97470',
    vest: '#6a7441', scarf: '#e19a43', trim: '#eee1ba', eye: '#493d2c', scale: 1.06, earTilt: 0.03, tailCurl: 0.18, accessory: 'radio',
  },
  rowan: {
    id: 'rowan', displayName: 'Rowan', role: 'Rescue pilot', fur: '#aea29b', muzzle: '#e4d9d1', innerEar: '#c98e8e',
    vest: '#a74837', scarf: '#f0c45c', trim: '#eee9d8', eye: '#5c6e75', scale: 1.1, earTilt: -0.07, tailCurl: 0.25, accessory: 'canteen',
  },
  juniper: {
    id: 'juniper', displayName: 'Juniper', role: 'Botanist', fur: '#c8916a', muzzle: '#eed4b7', innerEar: '#d58d82',
    vest: '#467f58', scarf: '#e784a2', trim: '#f5e8bd', eye: '#416c5d', scale: 0.98, earTilt: 0.09, tailCurl: 0.3, accessory: 'flower',
  },
  ember: {
    id: 'ember', displayName: 'Ember', role: 'Junior documentarian', fur: '#4f4c51', muzzle: '#aaa1a0', innerEar: '#a2707a',
    vest: '#b65d32', scarf: '#63b8bd', trim: '#eee1bd', eye: '#d09a4f', scale: 0.88, earTilt: 0.18, tailCurl: 0.38, accessory: 'camera',
  },
};

const Y_AXIS = new THREE.Vector3(0, 1, 0);
const FORWARD = new THREE.Vector3(0, 0, -1);

export function getRangerPupProfile(id: RangerPupId): RangerPupProfile {
  return { ...PROFILES[id] };
}

export class RangerPupCharacter {
  readonly root = new THREE.Group();
  readonly visual = new THREE.Group();
  readonly collision = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.42, 1.12, 4, 8),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  readonly rig: RangerPupRig;
  readonly profile: RangerPupProfile;
  readonly diagnostics: CharacterDiagnostics;

  private readonly materials: THREE.Material[] = [];
  private readonly lookTarget = new THREE.Vector3();
  private hasLookTarget = false;
  private motion: CharacterMotion = 'idle';
  private gesture: CharacterGesture = 'none';
  private expression: CharacterExpression;
  private speed = 0;
  private talk = 0;
  private blinkOverride = false;
  private phase = 0;

  constructor(id: RangerPupId, options: RangerPupOptions = {}) {
    this.profile = getRangerPupProfile(id);
    this.expression = options.initialExpression ?? 'neutral';
    this.root.name = `character:${id}`;
    this.visual.name = `${id}:visual`;
    this.collision.name = `${id}:collisionProxy`;
    this.collision.position.y = 1.05;
    this.collision.layers.set(1);
    this.root.add(this.visual, this.collision);
    this.rig = this.buildRig(options.castShadow ?? true);
    this.visual.scale.setScalar(this.profile.scale * (options.scale ?? 1));
    this.diagnostics = collectDiagnostics(this.visual, 23);
  }

  setAnimation(input: CharacterAnimationInput): void {
    if (input.motion !== undefined) this.motion = input.motion;
    if (input.gesture !== undefined) this.gesture = input.gesture;
    if (input.expression !== undefined) this.expression = input.expression;
    if (input.speed !== undefined) this.speed = THREE.MathUtils.clamp(input.speed, 0, 1.5);
    if (input.talk !== undefined) this.talk = THREE.MathUtils.clamp(input.talk, 0, 1);
    if (input.blink !== undefined) this.blinkOverride = input.blink;
    if (input.lookTarget !== undefined) {
      this.hasLookTarget = input.lookTarget !== null;
      if (input.lookTarget) this.lookTarget.copy(input.lookTarget);
    }
  }

  setLookTarget(target: THREE.Vector3 | null): void {
    this.hasLookTarget = target !== null;
    if (target) this.lookTarget.copy(target);
  }

  resetAnimationPhase(value = 0): void {
    this.phase = value;
  }

  update(deltaSeconds: number, elapsedSeconds: number): void {
    this.phase += deltaSeconds * (this.motion === 'run' ? 10.5 : this.motion === 'walk' ? 6.8 : 2.1);
    const locomotion = this.motion === 'idle' ? 0 : Math.max(0.18, this.speed);
    const stride = Math.sin(this.phase) * locomotion;
    const counter = Math.sin(this.phase + Math.PI) * locomotion;
    const bob = Math.abs(Math.sin(this.phase)) * locomotion;
    const sneak = this.motion === 'sneak' ? 0.16 : 0;

    this.rig.hips.position.y = 0.86 - bob * 0.045 - sneak;
    this.rig.hips.rotation.z = Math.sin(this.phase * 0.5) * locomotion * 0.025;
    this.rig.chest.rotation.x = sneak * 0.7 + Math.sin(this.phase * 2) * locomotion * 0.018;
    this.rig.leftHip.rotation.x = stride * 0.68;
    this.rig.rightHip.rotation.x = counter * 0.68;
    this.rig.leftKnee.rotation.x = Math.max(0, -stride) * 0.55;
    this.rig.rightKnee.rotation.x = Math.max(0, -counter) * 0.55;
    this.rig.leftShoulder.rotation.x = counter * 0.52;
    this.rig.rightShoulder.rotation.x = stride * 0.52;
    this.rig.leftElbow.rotation.x = -0.12 - Math.max(0, stride) * 0.32;
    this.rig.rightElbow.rotation.x = -0.12 - Math.max(0, counter) * 0.32;
    this.rig.leftAnkle.rotation.x = -stride * 0.12;
    this.rig.rightAnkle.rotation.x = -counter * 0.12;
    this.rig.tail.rotation.x = 0.3 + Math.sin(elapsedSeconds * 3.3 + this.profile.tailCurl) * 0.12;
    this.rig.tail.rotation.z = Math.sin(elapsedSeconds * 4.5 + this.profile.tailCurl) * (this.expression === 'happy' ? 0.34 : 0.16);

    this.applyGesture(elapsedSeconds);
    this.applyExpression(elapsedSeconds);
    this.applyLook();
  }

  dispose(): void {
    const geometries = new Set<THREE.BufferGeometry>();
    this.root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      geometries.add(object.geometry);
    });
    for (const geometry of geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    const collisionMaterial = this.collision.material;
    if (Array.isArray(collisionMaterial)) collisionMaterial.forEach((material) => material.dispose());
    else collisionMaterial.dispose();
  }

  private buildRig(castShadow: boolean): RangerPupRig {
    const fur = this.material('fur', this.profile.fur, 0.82);
    const muzzle = this.material('muzzle', this.profile.muzzle, 0.9);
    const innerEar = this.material('innerEar', this.profile.innerEar, 0.78);
    const vest = this.material('vest', this.profile.vest, 0.66);
    const scarf = this.material('scarf', this.profile.scarf, 0.54);
    const trim = this.material('trim', this.profile.trim, 0.48);
    const dark = this.material('darkDetail', '#2d2a2d', 0.38);
    const eye = this.material('eye', this.profile.eye, 0.22, 0.1);
    const white = this.material('eyeWhite', '#f8f3e8', 0.28);
    const metal = this.material('badgeMetal', '#dbc36b', 0.24, 0.48);

    const hips = joint(`${this.profile.id}:hips`, this.visual, 0, 0.86, 0);
    const pelvis = mesh('pelvis', new THREE.SphereGeometry(0.36, 16, 10), fur, castShadow, [0.92, 0.74, 0.76]);
    hips.add(pelvis);
    const chest = joint('chest', hips, 0, 0.54, 0);
    const torso = mesh('torso', new THREE.CapsuleGeometry(0.35, 0.5, 6, 12), fur, castShadow, [1.04, 1, 0.8]);
    torso.position.y = 0.03;
    chest.add(torso);

    const vestShell = mesh('rangerVest', new THREE.CapsuleGeometry(0.365, 0.38, 5, 12), vest, castShadow, [1.06, 0.9, 0.84]);
    vestShell.position.set(0, -0.02, 0.008);
    chest.add(vestShell);
    const vestPlacket = mesh('vestPlacket', new THREE.BoxGeometry(0.075, 0.43, 0.035), trim, castShadow);
    vestPlacket.position.set(0, 0, -0.327);
    chest.add(vestPlacket);
    const belt = mesh('utilityBelt', new THREE.TorusGeometry(0.335, 0.047, 6, 24), dark, castShadow, [1, 1, 0.82]);
    belt.rotation.x = Math.PI / 2;
    belt.position.y = -0.36;
    chest.add(belt);
    const badge = mesh('trailBadge', createShieldGeometry(0.12), metal, castShadow);
    badge.position.set(-0.16, 0.13, -0.356);
    badge.rotation.x = -0.05;
    chest.add(badge);

    const neck = joint('neck', chest, 0, 0.54, 0);
    const scarfBand = mesh('scarfBand', new THREE.TorusGeometry(0.245, 0.055, 6, 20), scarf, castShadow, [1, 1, 0.86]);
    scarfBand.rotation.x = Math.PI / 2;
    neck.add(scarfBand);
    const scarfTail = mesh('scarfTail', new THREE.ConeGeometry(0.1, 0.35, 3), scarf, castShadow);
    scarfTail.position.set(0.16, -0.18, 0.15);
    scarfTail.rotation.z = -0.3;
    neck.add(scarfTail);

    const head = joint('head', neck, 0, 0.31, -0.015);
    const cranium = mesh('cranium', new THREE.SphereGeometry(0.34, 18, 12), fur, castShadow, [0.94, 1.04, 0.86]);
    head.add(cranium);
    const cheekLeft = mesh('leftCheekTuft', new THREE.ConeGeometry(0.105, 0.29, 4), fur, castShadow);
    cheekLeft.position.set(-0.27, -0.04, -0.03);
    cheekLeft.rotation.z = 1.22;
    const cheekRight = cheekLeft.clone();
    cheekRight.name = 'rightCheekTuft';
    cheekRight.position.x = 0.27;
    cheekRight.rotation.z = -1.22;
    head.add(cheekLeft, cheekRight);

    const muzzleJoint = joint('muzzle', head, 0, -0.08, -0.27);
    const snout = mesh('snout', new THREE.CapsuleGeometry(0.145, 0.22, 5, 10), muzzle, castShadow, [1.2, 0.78, 1]);
    snout.rotation.x = Math.PI / 2;
    snout.position.z = -0.095;
    muzzleJoint.add(snout);
    const nose = mesh('nose', new THREE.SphereGeometry(0.095, 12, 8), dark, castShadow, [1.18, 0.72, 0.8]);
    nose.position.set(0, 0.025, -0.26);
    muzzleJoint.add(nose);
    const jaw = joint('jaw', muzzleJoint, 0, -0.09, -0.11);
    const jawMesh = mesh('lowerJaw', new THREE.CapsuleGeometry(0.1, 0.15, 4, 8), muzzle, castShadow, [1.15, 0.55, 0.9]);
    jawMesh.rotation.x = Math.PI / 2;
    jaw.add(jawMesh);

    const leftEye = this.buildEye('leftEye', head, -0.135, 0.09, white, eye, dark, castShadow);
    const rightEye = this.buildEye('rightEye', head, 0.135, 0.09, white, eye, dark, castShadow);
    const leftEar = this.buildEar('leftEar', head, -0.19, this.profile.earTilt, fur, innerEar, castShadow);
    const rightEar = this.buildEar('rightEar', head, 0.19, -this.profile.earTilt, fur, innerEar, castShadow);

    const leftShoulder = joint('leftShoulder', chest, -0.39, 0.29, 0);
    const rightShoulder = joint('rightShoulder', chest, 0.39, 0.29, 0);
    const leftElbow = this.buildArm('left', leftShoulder, fur, vest, dark, castShadow);
    const rightElbow = this.buildArm('right', rightShoulder, fur, vest, dark, castShadow);
    const leftWrist = leftElbow.userData.wrist as THREE.Group;
    const rightWrist = rightElbow.userData.wrist as THREE.Group;

    const leftHip = joint('leftHip', hips, -0.2, -0.12, 0);
    const rightHip = joint('rightHip', hips, 0.2, -0.12, 0);
    const leftKnee = this.buildLeg('left', leftHip, fur, dark, trim, castShadow);
    const rightKnee = this.buildLeg('right', rightHip, fur, dark, trim, castShadow);
    const leftAnkle = leftKnee.userData.ankle as THREE.Group;
    const rightAnkle = rightKnee.userData.ankle as THREE.Group;

    const tail = joint('tail', hips, 0, 0.03, 0.22);
    const tailCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -0.08, 0.3),
      new THREE.Vector3(0.04, 0.08, 0.58), new THREE.Vector3(0.12, 0.3 + this.profile.tailCurl * 0.2, 0.7),
    ]);
    const tailMesh = mesh('tailFur', new THREE.TubeGeometry(tailCurve, 18, 0.115, 7, false), fur, castShadow);
    tail.add(tailMesh);
    const tailTip = mesh('tailTip', new THREE.ConeGeometry(0.11, 0.3, 7), muzzle, castShadow);
    tailTip.position.set(0.12, 0.36 + this.profile.tailCurl * 0.2, 0.72);
    tailTip.rotation.x = -0.65;
    tail.add(tailTip);

    const backpack = joint('backpack', chest, 0, 0.05, 0.36);
    this.buildBackpack(backpack, vest, trim, dark, castShadow);
    const propSocket = joint('propSocket', rightWrist, 0, -0.17, 0);
    this.buildAccessory(propSocket, vest, trim, dark, metal, castShadow);

    return {
      hips, chest, neck, head, muzzle: muzzleJoint, jaw, leftEar, rightEar, leftEye, rightEye,
      leftShoulder, rightShoulder, leftElbow, rightElbow, leftWrist, rightWrist,
      leftHip, rightHip, leftKnee, rightKnee, leftAnkle, rightAnkle, tail, backpack, propSocket,
    };
  }

  private buildEye(
    name: string,
    parent: THREE.Group,
    x: number,
    y: number,
    white: THREE.Material,
    iris: THREE.Material,
    dark: THREE.Material,
    castShadow: boolean,
  ): THREE.Group {
    const eye = joint(name, parent, x, y, -0.292);
    const sclera = mesh(`${name}:sclera`, new THREE.SphereGeometry(0.082, 12, 8), white, castShadow, [0.78, 1.12, 0.5]);
    const irisMesh = mesh(`${name}:iris`, new THREE.SphereGeometry(0.04, 10, 7), iris, false, [0.78, 1.08, 0.32]);
    irisMesh.position.z = -0.058;
    const pupil = mesh(`${name}:pupil`, new THREE.SphereGeometry(0.018, 8, 6), dark, false, [0.75, 1.05, 0.25]);
    pupil.position.z = -0.076;
    eye.add(sclera, irisMesh, pupil);
    return eye;
  }

  private buildEar(
    name: string,
    parent: THREE.Group,
    x: number,
    tilt: number,
    fur: THREE.Material,
    inner: THREE.Material,
    castShadow: boolean,
  ): THREE.Group {
    const ear = joint(name, parent, x, 0.26, -0.01);
    ear.rotation.z = Math.sign(x) * (0.12 + tilt);
    const outer = mesh(`${name}:outer`, new THREE.ConeGeometry(0.17, 0.46, 4), fur, castShadow, [0.86, 1, 0.66]);
    outer.position.y = 0.19;
    const inset = mesh(`${name}:inner`, new THREE.ConeGeometry(0.095, 0.31, 3), inner, false, [0.86, 1, 0.45]);
    inset.position.set(0, 0.17, -0.105);
    inset.rotation.y = Math.PI;
    ear.add(outer, inset);
    return ear;
  }

  private buildArm(
    side: 'left' | 'right',
    shoulder: THREE.Group,
    fur: THREE.Material,
    sleeve: THREE.Material,
    dark: THREE.Material,
    castShadow: boolean,
  ): THREE.Group {
    const sign = side === 'left' ? -1 : 1;
    shoulder.rotation.z = sign * -0.1;
    const upper = mesh(`${side}UpperArm`, new THREE.CapsuleGeometry(0.105, 0.34, 5, 8), sleeve, castShadow);
    upper.position.y = -0.25;
    shoulder.add(upper);
    const elbow = joint(`${side}Elbow`, shoulder, 0, -0.5, 0);
    const lower = mesh(`${side}Forearm`, new THREE.CapsuleGeometry(0.088, 0.3, 5, 8), fur, castShadow);
    lower.position.y = -0.22;
    elbow.add(lower);
    const wrist = joint(`${side}Wrist`, elbow, 0, -0.44, 0);
    const paw = mesh(`${side}Paw`, new THREE.SphereGeometry(0.13, 10, 8), fur, castShadow, [1.05, 0.74, 0.9]);
    paw.position.y = -0.08;
    wrist.add(paw);
    for (let i = -1; i <= 1; i += 1) {
      const claw = mesh(`${side}Claw${i + 1}`, new THREE.ConeGeometry(0.018, 0.075, 5), dark, false);
      claw.position.set(i * 0.045, -0.12, -0.08);
      claw.rotation.x = Math.PI / 2;
      wrist.add(claw);
    }
    elbow.userData.wrist = wrist;
    return elbow;
  }

  private buildLeg(
    side: 'left' | 'right',
    hip: THREE.Group,
    fur: THREE.Material,
    boot: THREE.Material,
    trim: THREE.Material,
    castShadow: boolean,
  ): THREE.Group {
    const upper = mesh(`${side}Thigh`, new THREE.CapsuleGeometry(0.15, 0.35, 5, 9), fur, castShadow);
    upper.position.y = -0.29;
    hip.add(upper);
    const knee = joint(`${side}Knee`, hip, 0, -0.56, 0);
    const lower = mesh(`${side}Shin`, new THREE.CapsuleGeometry(0.12, 0.31, 5, 8), fur, castShadow);
    lower.position.y = -0.23;
    knee.add(lower);
    const ankle = joint(`${side}Ankle`, knee, 0, -0.45, 0);
    const bootMesh = mesh(`${side}TrailBoot`, new THREE.CapsuleGeometry(0.145, 0.18, 5, 9), boot, castShadow, [1.1, 0.7, 1.35]);
    bootMesh.rotation.x = Math.PI / 2;
    bootMesh.position.set(0, -0.08, -0.08);
    ankle.add(bootMesh);
    const bootBand = mesh(`${side}BootBand`, new THREE.TorusGeometry(0.135, 0.024, 5, 14), trim, false, [1, 1, 0.85]);
    bootBand.rotation.x = Math.PI / 2;
    bootBand.position.y = 0.04;
    ankle.add(bootBand);
    knee.userData.ankle = ankle;
    return knee;
  }

  private buildBackpack(parent: THREE.Group, body: THREE.Material, trim: THREE.Material, dark: THREE.Material, castShadow: boolean): void {
    const pack = mesh('fieldPack', new THREE.CapsuleGeometry(0.23, 0.34, 5, 10), body, castShadow, [1.1, 0.92, 0.56]);
    pack.rotation.x = Math.PI / 2;
    const flap = mesh('fieldPackFlap', new THREE.BoxGeometry(0.38, 0.24, 0.055), trim, castShadow);
    flap.position.set(0, 0.12, 0.21);
    const latch = mesh('fieldPackLatch', new THREE.BoxGeometry(0.07, 0.1, 0.045), dark, false);
    latch.position.set(0, 0.06, 0.255);
    parent.add(pack, flap, latch);
  }

  private buildAccessory(
    parent: THREE.Group,
    body: THREE.Material,
    trim: THREE.Material,
    dark: THREE.Material,
    metal: THREE.Material,
    castShadow: boolean,
  ): void {
    const type = this.profile.accessory;
    if (type === 'map') {
      const paper = mesh('foldedTrailMap', new THREE.BoxGeometry(0.31, 0.22, 0.018), trim, false);
      paper.rotation.z = 0.25;
      parent.add(paper);
    } else if (type === 'goggles') {
      const frame = mesh('goggleFrame', new THREE.TorusGeometry(0.09, 0.018, 6, 16), metal, castShadow, [1.7, 1, 1]);
      frame.rotation.y = Math.PI / 2;
      parent.add(frame);
    } else if (type === 'satchel') {
      const pouch = mesh('specimenSatchel', new THREE.BoxGeometry(0.29, 0.24, 0.14), body, castShadow);
      pouch.position.y = -0.08;
      parent.add(pouch);
    } else if (type === 'radio') {
      const radio = mesh('rangerRadio', new THREE.BoxGeometry(0.18, 0.3, 0.1), dark, castShadow);
      const aerial = mesh('radioAerial', new THREE.CylinderGeometry(0.012, 0.012, 0.22, 6), metal, false);
      aerial.position.set(0.06, 0.23, 0);
      parent.add(radio, aerial);
    } else if (type === 'canteen') {
      const bottle = mesh('trailCanteen', new THREE.CapsuleGeometry(0.1, 0.17, 4, 8), body, castShadow, [1, 1, 0.55]);
      parent.add(bottle);
    } else if (type === 'flower') {
      const stem = mesh('flowerStem', new THREE.CylinderGeometry(0.012, 0.014, 0.34, 6), body, false);
      const bloom = mesh('flowerBloom', new THREE.TorusKnotGeometry(0.05, 0.018, 28, 4, 2, 3), trim, false);
      bloom.position.y = 0.2;
      parent.add(stem, bloom);
    } else {
      const camera = mesh('storyCamera', new THREE.BoxGeometry(0.26, 0.18, 0.16), dark, castShadow);
      const lens = mesh('cameraLens', new THREE.CylinderGeometry(0.07, 0.08, 0.09, 12), metal, castShadow);
      lens.rotation.x = Math.PI / 2;
      lens.position.z = -0.12;
      parent.add(camera, lens);
    }
  }

  private material(name: string, color: THREE.ColorRepresentation, roughness: number, metalness = 0.02): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({ name: `${this.profile.id}:${name}`, color, roughness, metalness });
    this.materials.push(material);
    return material;
  }

  private applyGesture(elapsed: number): void {
    const wave = Math.sin(elapsed * 7.5);
    if (this.gesture === 'wave') {
      this.rig.rightShoulder.rotation.set(-2.25, 0, -0.25);
      this.rig.rightElbow.rotation.set(-0.75, 0, wave * 0.45);
      this.rig.rightWrist.rotation.z = wave * 0.38;
    } else if (this.gesture === 'point') {
      this.rig.rightShoulder.rotation.set(-1.5, 0.05, -0.08);
      this.rig.rightElbow.rotation.set(-0.08, 0, 0);
    } else if (this.gesture === 'cheer') {
      this.rig.leftShoulder.rotation.set(-2.6, 0, 0.22);
      this.rig.rightShoulder.rotation.set(-2.6, 0, -0.22);
      this.rig.leftElbow.rotation.x = -0.2;
      this.rig.rightElbow.rotation.x = -0.2;
    } else if (this.gesture === 'listen') {
      this.rig.leftShoulder.rotation.set(-1.9, 0, 0.42);
      this.rig.leftElbow.rotation.set(-1.35, 0, 0.25);
      this.rig.leftWrist.rotation.z = -0.4;
    }
  }

  private applyExpression(elapsed: number): void {
    const naturalBlink = Math.sin(elapsed * 0.73 + this.profile.scale * 5) > 0.986;
    const blink = this.blinkOverride || naturalBlink;
    const eyeY = blink ? 0.08 : 1;
    this.rig.leftEye.scale.y = THREE.MathUtils.lerp(this.rig.leftEye.scale.y, eyeY, 0.52);
    this.rig.rightEye.scale.y = THREE.MathUtils.lerp(this.rig.rightEye.scale.y, eyeY, 0.52);
    this.rig.jaw.rotation.x = this.talk * (0.12 + Math.abs(Math.sin(elapsed * 12.5)) * 0.22);

    const happy = this.expression === 'happy';
    const curious = this.expression === 'curious';
    const concerned = this.expression === 'concerned';
    const determined = this.expression === 'determined';
    this.rig.leftEar.rotation.x = concerned ? -0.45 : happy ? 0.12 : 0;
    this.rig.rightEar.rotation.x = concerned ? -0.45 : happy ? 0.12 : 0;
    this.rig.leftEar.rotation.z += curious ? -0.18 : determined ? 0.08 : 0;
    this.rig.rightEar.rotation.z += curious ? -0.18 : determined ? -0.08 : 0;
    this.rig.muzzle.rotation.x = happy ? -0.05 : concerned ? 0.05 : 0;
  }

  private applyLook(): void {
    if (!this.hasLookTarget) {
      this.rig.head.rotation.y *= 0.84;
      this.rig.head.rotation.x *= 0.84;
      return;
    }
    const headWorld = new THREE.Vector3();
    this.rig.head.getWorldPosition(headWorld);
    const direction = this.lookTarget.clone().sub(headWorld).normalize();
    const yaw = THREE.MathUtils.clamp(Math.atan2(direction.x, -direction.z), -0.65, 0.65);
    const pitch = THREE.MathUtils.clamp(Math.asin(direction.y), -0.35, 0.42);
    this.rig.head.rotation.y = THREE.MathUtils.lerp(this.rig.head.rotation.y, yaw, 0.18);
    this.rig.head.rotation.x = THREE.MathUtils.lerp(this.rig.head.rotation.x, -pitch, 0.18);
  }
}

export function createRangerPup(id: RangerPupId, options?: RangerPupOptions): RangerPupCharacter {
  return new RangerPupCharacter(id, options);
}

function joint(name: string, parent: THREE.Object3D, x: number, y: number, z: number): THREE.Group {
  const group = new THREE.Group();
  group.name = name;
  group.position.set(x, y, z);
  parent.add(group);
  return group;
}

function mesh(
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  castShadow: boolean,
  scale?: [number, number, number],
): THREE.Mesh {
  const object = new THREE.Mesh(geometry, material);
  object.name = name;
  object.castShadow = castShadow;
  object.receiveShadow = castShadow;
  if (scale) object.scale.set(...scale);
  return object;
}

function createShieldGeometry(size: number): THREE.ShapeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, size);
  shape.lineTo(size * 0.82, size * 0.58);
  shape.lineTo(size * 0.65, -size * 0.45);
  shape.lineTo(0, -size);
  shape.lineTo(-size * 0.65, -size * 0.45);
  shape.lineTo(-size * 0.82, size * 0.58);
  shape.closePath();
  return new THREE.ShapeGeometry(shape, 4);
}

function collectDiagnostics(root: THREE.Object3D, bones: number): CharacterDiagnostics {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  let meshes = 0;
  let triangles = 0;
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    meshes += 1;
    geometries.add(object.geometry);
    if (Array.isArray(object.material)) object.material.forEach((material) => materials.add(material));
    else materials.add(object.material);
    const indexCount = object.geometry.index?.count;
    const positionCount = object.geometry.attributes.position?.count ?? 0;
    triangles += Math.floor((indexCount ?? positionCount) / 3);
  });
  return { meshes, geometries: geometries.size, materials: materials.size, triangles, bones };
}

void Y_AXIS;
void FORWARD;
